import { parse } from 'date-fns'
import { useMemo, useState } from 'react'

import { FilterAccordion, FloatingContainerProps, formatDate } from '@avantstay/backoffice-core'
import { formatDateRange } from '@avantstay/format-date-range'

import { DatePicker, DatePickerProps, useDatePickerValue } from '../../DatePicker'
import { FiltersSharedProps } from '../Filters.types'
import * as S from './DatePickerButton.styles'

interface DatePickerButtonProps
  extends FiltersSharedProps, Pick<DatePickerProps, 'views' | 'presets'> {
  title: string
  initialValue?: string
  placement?: FloatingContainerProps['placement']
  allowFlip?: boolean
  disabled?: boolean
  onChange: (value?: string) => void
}

export const DatePickerButton = ({
  title,
  initialValue,
  placement = 'top',
  allowFlip,
  views,
  presets,
  isSidebarFilter,
  disabled,
  onChange,
  ...otherProps
}: DatePickerButtonProps) => {
  const [isDatePickerVisible, setIsDatePickerVisible] = useState(false)

  const { value, metadata, handleChange, handleClear } = useDatePickerValue({
    initialValue,
    onChange: value => {
      onChange(value)
      setIsDatePickerVisible(false)
    },
  })

  const content = useMemo(() => {
    if (!initialValue) return null

    const [startDate, endDate] = initialValue.split('_')
    const parsedStartDate = startDate ? parse(startDate, 'yyyy-MM-dd', new Date()) : undefined
    const parsedEndDate = endDate ? parse(endDate, 'yyyy-MM-dd', new Date()) : undefined

    if (parsedStartDate && parsedEndDate) return formatDateRange(parsedStartDate, parsedEndDate)

    return parsedStartDate
      ? formatDate(parsedStartDate, 'MMM dd, yyyy')
      : formatDate(parsedEndDate, 'MMM dd, yyyy')
  }, [initialValue])

  const renderDatePicker = () => {
    return (
      <DatePicker
        value={value}
        metadata={metadata}
        views={views}
        presets={presets}
        onChange={handleChange}
        onCancel={() => setIsDatePickerVisible(false)}
      />
    )
  }

  return (
    <FilterAccordion
      isDateRange
      title={title}
      filterValue={value}
      isSidebarFilter={isSidebarFilter}
      shouldShowClearButton={false}
      onClearFilter={handleClear}
      {...otherProps}
    >
      {isSidebarFilter ? (
        renderDatePicker()
      ) : (
        <S.Container
          placement={placement}
          allowFlip={allowFlip}
          open={isDatePickerVisible}
          positioningTarget={
            <S.ReadOnlyInput
              small
              disabled={disabled}
              onClick={() => {
                setIsDatePickerVisible(!isDatePickerVisible)
              }}
            >
              <span>{title}</span>
              {content}
              <S.DropdownIcon $isActive={isDatePickerVisible} />
            </S.ReadOnlyInput>
          }
          onClickOut={() => setIsDatePickerVisible(false)}
        >
          {renderDatePicker()}
        </S.Container>
      )}
    </FilterAccordion>
  )
}
